import React, { useState, useRef } from 'react';
import AttendanceReport from './Attendance/AttendanceReport/AttendanceReport';
import AttendanceReportByStudent from './Attendance/AttendanceReportByStudent';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import styled from 'styled-components';

const StyledReportPage = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 60px;
  min-height: 600px;

  @media screen and (max-width: 1024px) {
    flex-direction: column;
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
  }

  @media screen and (max-width: 480px) {
    padding: 5px;
  }
`;

const StyledFiltersArea = styled.div`
  flex: 0.5;
  margin-right: 20px;
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  min-height: 600px;

  @media screen and (max-width: 1024px) {
    margin-right: 0;
    margin-bottom: 20px;
    flex: none;
    width: 100%;
  }

  @media screen and (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 20px;
    flex: none;
    width: 100%;
  }
`;

const StyledReportDisplayArea = styled.div`
  flex: 2;
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (max-width: 1024px) {
    width: 100%;
  }

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

const StyledNoReport = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--secondary-color);
  font-size: 1.2rem;
`;

const StyledAutoDownloadToggle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  label {
    display: flex;
    align-items: center;
    font-weight: normal;
    color: var(--text-color);
    font-size: 14px;
  }

  input[type="checkbox"] {
    margin-right: 8px;
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  .switch {
    position: relative;
    display: inline-block;
    width: 34px;
    height: 20px;
    margin-right: 8px;
  }

  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.4s;
    border-radius: 34px;
  }

  .slider:before {
    position: absolute;
    content: "";
    height: 12px;
    width: 12px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }

  input:checked + .slider {
    background-color: var(--primary-color);
  }

  input:checked + .slider:before {
    transform: translateX(14px);
  }
`;

const StyledFiltersReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  label {
    display: block;
    margin-bottom: 10px;
    font-weight: bold;
    color: var(--text-color);
  }

  select {
    width: 100%;
    padding: 8px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 16px;
    background-color: var(--background-color);
    color: var(--text-color);
  }

  select:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
  }
`;

const StyledReportButtonsActions = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: center;
  gap: 10px;
  padding-top: 20px;
`;

const StyledClearFiltersButton = styled.button`
  background-color: var(--secondary-color);
  color: var(--header-color);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 400;
  padding: 10px 20px;
  margin-top: 10px;
  margin-right: 10px;
  align-self: flex-end;

  &:hover {
    background-color: var(--primary-color);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const StyledGenerateReportButton = styled.button`
  background-color: var(--primary-color);
  color: var(--header-color);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 400;
  padding: 10px 20px;

  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const StyledClearReportButton = styled.button`
  background-color: var(--secondary-color);
  color: var(--header-color);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 20px;
  align-self: flex-end;

  &:hover {
    background-color: var(--primary-color);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

function ReportPage({ userName }) {
  const [reportType, setReportType] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPdf, setShowPdf] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoDownload, setAutoDownload] = useState(false);

  const reportRefs = {
    'student-attendance': useRef(),
    'student-attendance-grouped': useRef(),
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    if (reportRefs[reportType]?.current) {
      await reportRefs[reportType].current.generateReport();
    }
    setLoading(false);
  };

  const handleReportGenerated = (url) => {
    setPdfUrl(url);
    setShowPdf(true);

    if (autoDownload) {
      // Baixa o arquivo automaticamente
      const reportName = reportType === 'student-attendance' ? 'Relatorio-de-Presenca' :
        reportType === 'student-attendance-grouped' ? 'Relatorio-de-Chamada-Agrupado' :
          'Relatorio';
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportName}-${new Date().toISOString().replace(/T/, '_').replace(/:/g, '-').split('.')[0]}.pdf`;
      link.click();
    }
  };

  const handleClearReport = () => {
    setPdfUrl(null);
    setShowPdf(false);
  };

  const handleClearFilters = () => {
    if (reportRefs[reportType]?.current) {
      reportRefs[reportType].current.clearFilters();
    }
  };

  const renderReportFilters = () => {
    switch (reportType) {
      case 'student-attendance':
        return (
          <AttendanceReport
            ref={reportRefs['student-attendance']}
            onGenerateReport={handleReportGenerated}
          />
        );
      case 'student-attendance-grouped':
        return (
          <AttendanceReportByStudent
            ref={reportRefs['student-attendance-grouped']}
            onGenerateReport={handleReportGenerated}
            userName={userName}
          />
        );
      default:
        return null;
    }
  };

  return (
    <StyledReportPage>
      <StyledFiltersArea>
        <h2>Filtros</h2>
        <StyledAutoDownloadToggle>
          <label>
            <div className="switch">
              <input
                type="checkbox"
                checked={autoDownload}
                onChange={() => setAutoDownload(!autoDownload)}
              />
              <span className="slider"></span>
            </div>
            Baixar automaticamente
          </label>
        </StyledAutoDownloadToggle>
        <StyledFiltersReportContainer>
          <label>
            Módulo:
            <select value={reportType} onChange={(e) => setReportType(e.target.value)}>
              <option value="">Selecione</option>
              <optgroup label="Alunos">
                <option value="student-attendance">Relatório de Presença</option>
                <option value="student-attendance-grouped">Relatório de Chamada Agrupado</option>
              </optgroup>
              {/* <optgroup label="Turmas">
                <option value="class-attendance">Relatório de Presença</option>
                <option value="class-performance">Relatório de Desempenho</option>
              </optgroup> */}
            </select>
          </label>
          {renderReportFilters()}
        </StyledFiltersReportContainer>
        <StyledReportButtonsActions>
          <StyledClearFiltersButton onClick={handleClearFilters}>Limpar Filtros</StyledClearFiltersButton>
          <StyledGenerateReportButton onClick={handleGenerateReport}>Gerar Relatório</StyledGenerateReportButton>
          {showPdf && pdfUrl && (
            <StyledClearReportButton onClick={handleClearReport}>Limpar Relatório</StyledClearReportButton>
          )}
        </StyledReportButtonsActions>
      </StyledFiltersArea>
      <StyledReportDisplayArea>
        {loading ? (
          <LoadingSpinner />
        ) : showPdf && pdfUrl ? (
          <iframe
            src={pdfUrl}
            title="Relatório"
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
          />
        ) : (
          <StyledNoReport>Nenhum relatório exibido</StyledNoReport>
        )}
      </StyledReportDisplayArea>
    </StyledReportPage>
  );
}

export default ReportPage;
